import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { formatDateToMMDDYYYY } from '@zg-rentals/date-utils';

import { Alert, Box, Button, Form, Heading, Modal } from '@zillow/constellation-10';

import {
  RentalManagerRenterRoleWebApiHandler_update,
  RentalManagerRenterWebApiHandler_addOrUpdate,
  RentalManagerUserInfoWebApiHandler_get,
  RenterRoleType,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import { DatarouterError } from '@zg-rentals/web-base';

import FormFieldLeaseTerm, { LEASE_TERM_OPTIONS } from '../FormFieldLeaseTerm';

import type { Client } from '@zg-rentals/http-client';
import type { TenantFormProps } from '../TenantForm';

import type {
  RentalManagerRenterAddOrUpdateRenterDto,
  RentalManagerRenterAddOrUpdateRentersRequestDto,
  RentalManagerRenterAddOrUpdateRentersResponseDto,
  RentalManagerRenterContactTenantDto,
  RentalManagerRenterRoleUpdateRequestDto,
  RentalManagerRenterRoleUpdateResponseWebDto,
  RenterInfoRenterAddedDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import { logError } from '@zg-rentals/log-error';

export type ExtendedRentalManagerRenterContactAddRenterDto = RentalManagerRenterAddOrUpdateRenterDto & {
  renterId: string;
};

export type ModalEditTenantFormProps = {
  client: Client;
  headerTitle?: string;
  isOpen: boolean;
  isEditMode: boolean;
  listingAlias: string;
  primaryCTALabel?: string;
  renter: RentalManagerRenterContactTenantDto;
  onClose: () => void;
  onSuccess?: ({
    addedRenters,
    isEditMode,
  }: {
    addedRenters?: Array<RenterInfoRenterAddedDto>;
    isEditMode: boolean;
  }) => void | null;
  onError?: (error: DatarouterError) => void | null;
};

type DropdownSelectLeaseTermData = {
  value: string;
  meta: Record<string, unknown>;
  calls: number;
};

const MODAL_EDIT_TENANT_FORM_CONTENT = {
  HEADER_TITLE: 'Edit Tenancy',
  PRIMARY_CTA_LABEL: 'Save',
} as const;

const OPTIONS = { LIMIT_TAGS_SHOWN: 5, PLACEHOLDER: 'Select tenants', DEFAULT_RENTER_ID_AS_STRING: '' } as const;

const EVENT_IDS = {
  TENANCY_PERIOD_CREATED: '8369',
  TENANCY_PERIOD_EDIT_SUCCESS: '8371',
} as const;

const defaultDate = '';

const getMsFromDate = (date: string) => {
  return date ? new Date(date).getTime() : null;
};

const getInitialLeaseTermValue = ({ endDateMs, isEditMode }: { endDateMs?: number; isEditMode: boolean }): string => {
  if (isEditMode === false) {
    return LEASE_TERM_OPTIONS[0].value;
  }

  return endDateMs ? LEASE_TERM_OPTIONS[0].value : LEASE_TERM_OPTIONS[1].value;
};

/**
 * @param {ModalEditTenantFormProps} props
 *
 * @param {Client} props.client - The client to use for the query
 * @param {RentalManagerRenterContactTenantDto} props.renter - The renter to edit
 * @param {string} props.headerTitle - The title of the modal
 * @param {string} props.listingAlias - The listing alias to use for the query
 * @param {string} props.primaryCTALabel - The label of the primary CTA button
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {boolean} props.isEditMode - Whether the modal is in edit mode
 * @param {() => void} props.onClose - The function to call when the modal is closed
 * @param {({ addedRenters, isEditMode }: { addedRenters?: Array<RenterInfoRenterAddedDto>; isEditMode: boolean }) => void} props.onSuccess - The function to call when the tenant is successfully edited
 * @param {(error: DatarouterError) => void} props.onError - The function to call when the tenant is not successfully edited
 *
 * @returns {JSX.Element}
 */

const ModalFormFieldLeaseTerm = ({
  client,
  headerTitle = MODAL_EDIT_TENANT_FORM_CONTENT.HEADER_TITLE,
  isOpen,
  isEditMode,
  listingAlias,
  primaryCTALabel = MODAL_EDIT_TENANT_FORM_CONTENT.PRIMARY_CTA_LABEL,
  renter,
  onClose,
  onSuccess,
  onError,
}: ModalEditTenantFormProps): JSX.Element => {
  const [startDate, setStartDate] = useState<string>(
    renter?.displayTenancy?.startDateMs
      ? formatDateToMMDDYYYY(new Date(renter.displayTenancy.startDateMs))
      : defaultDate,
  );

  const [endDate, setEndDate] = useState<string>(
    renter?.displayTenancy?.endDateMs ? formatDateToMMDDYYYY(new Date(renter.displayTenancy.endDateMs)) : defaultDate,
  );

  const [comboboxLeaseTermData, setComboboxLeaseTermData] = useState<DropdownSelectLeaseTermData>({
    value: getInitialLeaseTermValue({ endDateMs: renter?.displayTenancy?.endDateMs, isEditMode }),
    meta: {},
    calls: 0,
  });

  const [serverError, setServerError] = useState<string | null>(null);

  const [formError, setFormError] = useState<Record<string, string>>({
    startDate: '',
    endDate: '',
  });

  const [tenantFormList, setTenantFormList] = useState<Record<string, TenantFormProps>>({});

  const previousEndDateRef = useRef<string>(endDate);

  const getTenantFormValidation = (tenantFormList: Record<string, TenantFormProps>) => {
    const hasFieldError = Object.values(tenantFormList).some((tenantForm) => tenantForm.hasFieldError);

    return hasFieldError;
  };

  const title = isEditMode ? 'Edit Tenancy' : 'Add Tenancy';

  const tenantFormListLength = Object.keys(tenantFormList).length || 0;
  const isFixedTermLeaseTerm = comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[0].value;
  const isEndDateRequired = isFixedTermLeaseTerm;

  const isPrimaryCTAButtonDisabled =
    !startDate ||
    (isEndDateRequired && !endDate) ||
    tenantFormListLength === 0 ||
    getTenantFormValidation(tenantFormList) === true;

  useEffect(() => {
    if (isOpen) {
      updateFormData(renter);
      clearServerError();
    }
  }, [isOpen, renter]);

  useEffect(() => {
    // keep a reference to the previous end date
    if (comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[0].value) {
      setEndDate(previousEndDateRef.current);
    }

    // month-to-month lease term
    if (comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[1].value) {
      setEndDate(defaultDate);
    }
  }, [comboboxLeaseTermData.value]);

  /**
   * Validate form fields
   */
  useEffect(() => {
    // set the previous end date before validating the form
    if (endDate && endDate !== defaultDate) {
      previousEndDateRef.current = endDate;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchUser = async () => {
    const response = await RentalManagerUserInfoWebApiHandler_get({}, client).catch((error) => {
      throw error;
    });

    return response;
  };

  const updateFormData = (renter: RentalManagerRenterContactTenantDto) => {
    setStartDate(
      renter?.displayTenancy?.startDateMs
        ? formatDateToMMDDYYYY(new Date(renter.displayTenancy.startDateMs))
        : defaultDate,
    );

    const newEndDate = renter?.displayTenancy?.endDateMs
      ? formatDateToMMDDYYYY(new Date(renter.displayTenancy.endDateMs))
      : defaultDate;
    setEndDate(newEndDate);

    setComboboxLeaseTermData({
      value: getInitialLeaseTermValue({ endDateMs: renter?.displayTenancy?.endDateMs, isEditMode }),
      meta: {},
      calls: 0,
    });

    replaceTenantInTenantFormList(getFormattedRenter(renter));
  };

  const clearServerError = useCallback(() => {
    setServerError(null);
  }, []);

  const replaceTenantInTenantFormList = (renter: RentalManagerRenterAddOrUpdateRenterDto) => {
    const tenantFormProps: TenantFormProps = {
      id: 1,
      title: 'Personal Info',
      renter,
    };

    setTenantFormList({
      1: tenantFormProps,
    });
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      onClose?.();
    }
  };

  const handleComboboxLeaseTermChange = useCallback(
    (value: string, meta: Record<string, unknown>) => {
      return setComboboxLeaseTermData({ value, meta, calls: comboboxLeaseTermData.calls + 1 });
    },
    [comboboxLeaseTermData.calls],
  );

  const getFormattedRenter = (renter: RentalManagerRenterContactTenantDto) => {
    return {
      firstName: renter.firstName,
      lastName: renter.lastName,
      phoneNumber: renter.phoneNumber,
      renterId: renter.renterId,
      rawEmailAddress: renter.emailAddress,
      role: {
        type: 'TENANT',
        roleStartDateMs: renter.displayTenancy.startDateMs,
        roleEndDateMs: renter.displayTenancy.endDateMs,
      },
    } as RentalManagerRenterAddOrUpdateRenterDto;
  };

  const getRenterRolesFormattedData = () => {
    const postData: RentalManagerRenterRoleUpdateRequestDto = {
      renterRolesToUpdate: [
        {
          renterRoleId: renter.displayTenancy.renterRoleId,
          newRole: RenterRoleType.TENANT,
          newStartDateMs: getMsFromDate(startDate) ?? 0,
          newEndDateMs: !endDate ? undefined : (getMsFromDate(endDate) ?? 0),
        },
      ],
    };

    // remove undefined end date to handle month to month lease term
    if (postData.renterRolesToUpdate[0].newEndDateMs === undefined) {
      delete postData.renterRolesToUpdate[0].newEndDateMs;
    }

    return postData;
  };

  const getAddOrUpdateRentersFormattedData = async () => {
    const response = await fetchUser();

    const { userToken = null } = response ?? {};

    const formattedData: RentalManagerRenterAddOrUpdateRentersRequestDto = {
      listingAlias,
      referenceId: userToken ? `zillow_${userToken}` : 'zillow_0000000000',
      referenceIdType: 'manualEntryId',
      renters: Object.values(tenantFormList).map(({ renter }) => {
        // remove renterId if it is empty
        if (renter.renterId === OPTIONS.DEFAULT_RENTER_ID_AS_STRING) {
          delete renter.renterId;
        }

        // append role data to all items
        renter.role = {
          type: RenterRoleType.TENANT,
          roleStartDateMs: getMsFromDate(startDate) ?? 0,
          roleEndDateMs: !endDate ? undefined : (getMsFromDate(endDate) ?? 0),
        };

        // remove undefined end date to handle month to month lease term
        if (renter.role.roleEndDateMs === undefined) {
          delete renter.role.roleEndDateMs;
        }

        return renter;
      }),
    };

    return formattedData;
  };

  const addRenters = async (postData: RentalManagerRenterAddOrUpdateRentersRequestDto) => {
    const response = await RentalManagerRenterWebApiHandler_addOrUpdate(postData, client).catch((error) => {
      throw error;
    });

    return response as RentalManagerRenterAddOrUpdateRentersResponseDto;
  };

  const updateRenterRoles = async () => {
    const postData = getRenterRolesFormattedData();

    const response = await RentalManagerRenterRoleWebApiHandler_update(postData, client).catch((error) => {
      throw error;
    });

    return response as RentalManagerRenterRoleUpdateResponseWebDto;
  };

  const handlePrimaryCTAClick = async () => {
    clearServerError();

    if (isEditMode) {
      updateRenterRoles().then(handleUpdateRenterRolesSuccess).catch(handleUpdateRenterRolesError);

      return;
    }

    const formattedData = await getAddOrUpdateRentersFormattedData();

    addRenters(formattedData).then(handleAddRentersSuccess).catch(handleAddRentersError);
  };

  const handleAddRentersSuccess = (response: RentalManagerRenterAddOrUpdateRentersResponseDto) => {
    onSuccess?.({ addedRenters: response?.rentersAdded, isEditMode });
    handleClose(false);

    return response;
  };

  const handleUpdateRenterRolesSuccess = () => {
    onSuccess?.({ isEditMode });
    handleClose(false);
  };

  const handleAddRentersError = (error: DatarouterError) => {
    const displayError = DatarouterError.getDisplayMessage(error);
    let errorMessage = displayError;

    // displayError might only be a string that can not be converted via JSON.parse
    try {
      const parsed = JSON.parse(displayError);

      if (parsed?.error?.message) {
        errorMessage = parsed.error.message;
      }
    } catch {
      // displayError is not JSON, use as is
    }

    const capitalizedError =
      typeof errorMessage === 'string' && errorMessage.length > 0
        ? errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1)
        : 'An unexpected error occurred';

    setServerError(capitalizedError);
    onError?.(new DatarouterError(capitalizedError, 'EDIT_RENTERS_ERROR', 500));

    logError({
      error: new Error(`Error editing tenants: ${capitalizedError}`),
      errorType: 'AddOrUpdateRentersError',
      context: 'ModalFormFieldLeaseTerm@handleAddRentersError',
    });
  };

  const handleUpdateRenterRolesError = (error: DatarouterError) => {
    const displayError = DatarouterError.getDisplayMessage(error);
    let errorMessage = displayError;

    // displayError might only be a string that can not be converted via JSON.parse
    try {
      const parsed = JSON.parse(displayError);

      if (parsed?.error?.message) {
        errorMessage = parsed.error.message;
      }
    } catch {
      // displayError is not JSON, use as is
    }

    const capitalizedError =
      typeof errorMessage === 'string' && errorMessage.length > 0
        ? errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1)
        : 'An unexpected error occurred';

    setServerError(capitalizedError);
    onError?.(new DatarouterError(capitalizedError, 'EDIT_RENTERS_ERROR', 500));

    logError({
      error: new Error(`Error updating tenants: ${capitalizedError}`),
      errorType: 'UpdateRentersError',
      context: 'ModalFormFieldLeaseTerm@handleUpdateRenterRolesError',
    });
  };

  const renderBody = () => {
    return (
      <Box data-testid="ModalFormFieldLeaseTerm">
        {serverError && (
          <>
            <Box
              data-testid="ErrorContainer"
              css={{
                marginBottom: 'loose',
              }}
            >
              <Alert tone="warning">{serverError}</Alert>
            </Box>
          </>
        )}

        <Form>
          <Box data-testid="FormDatesContainer">
            <FormFieldLeaseTerm
              startDate={startDate}
              endDate={endDate}
              comboboxLeaseTermData={comboboxLeaseTermData}
              formError={formError}
              isEndDateRequired={isEndDateRequired}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onLeaseTermChange={handleComboboxLeaseTermChange}
              onFormErrorChange={setFormError}
            />
          </Box>
        </Form>
      </Box>
    );
  };

  return (
    <>
      <Modal
        open={isOpen}
        size="md"
        scrollDocument={false}
        header={<Heading level={2}>{title}</Heading>}
        body={renderBody()}
        footer={
          <Button
            data-eventid={isEditMode ? EVENT_IDS.TENANCY_PERIOD_EDIT_SUCCESS : EVENT_IDS.TENANCY_PERIOD_CREATED}
            tone="brand"
            disabled={isPrimaryCTAButtonDisabled}
            onClick={handlePrimaryCTAClick}
          >
            {primaryCTALabel}
          </Button>
        }
        onOpenChange={handleClose}
      />
    </>
  );
};

export default memo(ModalFormFieldLeaseTerm);
